import { Card } from '../../../components/ui/Card';
import { formatCurrency } from '../../../lib/utils';
import { DollarSign, ShoppingCart, CreditCard, TrendingUp } from 'lucide-react';

interface ResumenFinancieroProps {
  ventasHoy: number;
  comprasHoy: number;
  gastosHoy: number;
  isLoading?: boolean;
}

export function ResumenFinanciero({ ventasHoy, comprasHoy, gastosHoy, isLoading = false }: ResumenFinancieroProps) {
  const balance = ventasHoy - comprasHoy - gastosHoy;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="p-4 animate-pulse">
            <div className="h-16 bg-gray-200 rounded"></div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Ventas */}
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Ventas Hoy</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(ventasHoy)}</p>
            <p className="text-xs text-gray-400">{ventasHoy > 0 ? ' Ingresos' : 'Sin ventas'}</p>
          </div>
          <div className="p-3 bg-blue-100 rounded-lg">
            <DollarSign size={24} className="text-blue-600" />
          </div>
        </div>
      </Card>

      {/* Compras */}
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Compras</p>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(comprasHoy)}</p>
            <p className="text-xs text-gray-400">{comprasHoy > 0 ? ' Egresos' : 'Sin compras'}</p>
          </div>
          <div className="p-3 bg-orange-100 rounded-lg">
            <ShoppingCart size={24} className="text-orange-600" />
          </div>
        </div>
      </Card>

      {/* Gastos */}
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Gastos Hoy</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(gastosHoy)}</p>
            <p className="text-xs text-gray-400">{gastosHoy > 0 ? ' Egresos' : 'Sin gastos'}</p>
          </div>
          <div className="p-3 bg-red-100 rounded-lg">
            <CreditCard size={24} className="text-red-600" />
          </div>
        </div>
      </Card>

      {/* Balance */}
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Balance del Día</p>
            <p className={`text-2xl font-bold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(balance)}
            </p>
            <p className="text-xs text-gray-400">
              {balance >= 0 ? ' Positivo' : ' Negativo'}
            </p>
          </div>
          <div className={`p-3 rounded-lg ${balance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
            <TrendingUp size={24} className={balance >= 0 ? 'text-green-600' : 'text-red-600'} />
          </div>
        </div>
      </Card>
    </div>
  );
}